import * as React from 'react';
import {
  ChevronRight,
  Plus,
  Sparkles,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import { ImagePreviewModal } from 'features/DigitalMarketing/components/ImagePreviewModal';
import { ManagementFilterBar } from 'features/DigitalMarketing/components/ManagementFilterBar';
import {
  buildEditWebsiteContentPath,
  EDIT_WEBSITE_CONTENT_BASE_PATH,
} from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import {
  extractUploadedUrl,
  getApiErrorMessage,
  getResponseList,
} from 'features/DigitalMarketing/utils/apiResponse';
import httpclient from 'libs/Axios';
import { PaginationBar } from 'core/components/PaginationBar';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMInput,
  DMLabel,
  DMText,
} from '../../../components/Primitives';

const instituteGalleryApi = {
  stats: '/master/instituteGallery/stats',
  list: '/master/instituteGallery/list',
  upsert: '/master/instituteGallery/upsert',
  publish: (id: number, isPublished: boolean) =>
    `/master/instituteGallery/publish/${id}?is_web_published=${isPublished}`,
  delete: (id: number) =>
    `/master/instituteGallery/delete?institute_gallery_id=${id}`,
};

type InfrastructurePhotoCategory = {
  indexLabel: string;
  slug: string;
  imageType: 'LIBRARY' | 'CLASS' | 'SCHOOL' | 'PLAYGROUND' | 'OTHER';
  title: string;
  description: string;
  photos: number;
  published: number;
  draft: number;
};

const photoCategories: InfrastructurePhotoCategory[] = [
  {
    indexLabel: '01',
    slug: 'school-building',
    imageType: 'SCHOOL',
    title: 'School Building',
    description: 'Photo of your school from the out side',
    photos: 3,
    published: 2,
    draft: 1,
  },
  {
    indexLabel: '02',
    slug: 'classroom',
    imageType: 'CLASS',
    title: 'Classsroom',
    description: 'Picture of an student learning in class',
    photos: 2,
    published: 1,
    draft: 1,
  },
  {
    indexLabel: '03',
    slug: 'library',
    imageType: 'LIBRARY',
    title: 'Library',
    description: 'Books,study tables and quiet reading spots',
    photos: 0,
    published: 0,
    draft: 0,
  },
  {
    indexLabel: '04',
    slug: 'playground',
    imageType: 'PLAYGROUND',
    title: 'Playground',
    description: 'Outdoors games, sports fields and play areas.',
    photos: 0,
    published: 0,
    draft: 0,
  },
  {
    indexLabel: '05',
    slug: 'others',
    imageType: 'OTHER',
    title: 'Others',
    description: 'Books,study tables and quiet reading spots',
    photos: 0,
    published: 0,
    draft: 0,
  },
];

type GalleryStatsItem = {
  IMAGE_TYPE?: string;
  total?: number;
  published?: number;
  unpublished?: number;
};

const buildCategoriesWithStats = (stats: GalleryStatsItem[]) => {
  const statsByImageType = new Map(
    stats.map(item => [String(item.IMAGE_TYPE || '').toUpperCase(), item])
  );

  return photoCategories.map(category => {
    const categoryStats = statsByImageType.get(category.imageType);

    return {
      ...category,
      photos: Number(categoryStats?.total || 0),
      published: Number(categoryStats?.published || 0),
      draft: Number(categoryStats?.unpublished || 0),
    };
  });
};

const InfrastructurePhotoCard = ({
  item,
}: {
  item: InfrastructurePhotoCategory;
}) => {
  const navigate = useNavigate();

  const handleManagePhotos = () => {
    navigate(
      buildEditWebsiteContentPath(`upload-infrastructure-photos/${item.slug}`),
      {
        state: { category: item },
      }
    );
  };

  return (
    <DMDiv
      as="article"
      className="flex min-h-[30vh] flex-col rounded-[8px] border border-gray-200 bg-white px-[1.55vw] py-[2.35vh] shadow-[0_8px_24px_rgba(0,0,0,0.14)] transition-shadow hover:shadow-[0_10px_28px_rgba(0,0,0,0.18)]"
    >
      <DMText
        as="span"
        className="text-[3.7vh] font-bold leading-none text-gray-300"
      >
        {item.indexLabel}
      </DMText>

      <DMDiv className="mt-[2.5vh] min-w-0">
        <DMText
          as="h2"
          className="truncate text-[2.45vh] font-bold leading-tight text-gray-950"
        >
          {item.title}
        </DMText>
        <DMText className="mt-[0.7vh] line-clamp-2 text-[1.55vh] font-medium leading-snug text-gray-500">
          {item.description}
        </DMText>
      </DMDiv>

      <DMDiv className="mt-auto border-y border-gray-300 py-[1.65vh]">
        <DMDiv className="grid grid-cols-3 gap-[1vw]">
          <DMDiv>
            <DMText className="text-[2.45vh] font-bold leading-none text-[#4B358C]">
              {item.photos}
              <DMText
                as="span"
                className="text-[1.65vh] font-medium text-gray-500"
              >
                /10
              </DMText>
            </DMText>
          </DMDiv>
          <DMDiv>
            <DMText className="text-[2.45vh] font-bold leading-none text-emerald-500">
              {item.published}
            </DMText>
          </DMDiv>
          <DMDiv>
            <DMText className="text-[2.45vh] font-bold leading-none text-amber-700">
              {item.draft}
            </DMText>
          </DMDiv>
        </DMDiv>
        <DMDiv className="mt-[1.35vh] grid grid-cols-3 gap-[1vw] text-[1.35vh] font-medium uppercase leading-none text-gray-600">
          <DMText as="span">Photos</DMText>
          <DMText as="span">Published</DMText>
          <DMText as="span">Draft</DMText>
        </DMDiv>
      </DMDiv>

      <DMButton
        type="button"
        onClick={handleManagePhotos}
        className="flex items-center justify-between pt-[2vh] text-left text-[1.7vh] font-bold leading-none text-blue-600 transition-colors hover:text-blue-700"
      >
        <DMText as="span">Manage Photos</DMText>
        <ChevronRight className="h-[2.1vh] w-[2.1vh]" strokeWidth={2.4} />
      </DMButton>
    </DMDiv>
  );
};

type PhotoStatus = 'Live' | 'Draft';

type InfrastructurePhotoItem = {
  id: number;
  src: string;
  status: PhotoStatus;
};

type PhotoFilter = 'all' | PhotoStatus;

const mapGalleryImageToPhoto = (item: any): InfrastructurePhotoItem => ({
  id: Number(
    item?.INSTITUTE_GALLERY_ID || item?.institute_gallery_id || item?.id || 0
  ),
  src: item?.IMAGE_URL || item?.image_url || '',
  status: item?.IS_WEB_PUBLISHED || item?.is_web_published ? 'Live' : 'Draft',
});

const photoFilterOptions = [
  { label: 'All Photos', value: 'all' as PhotoFilter },
  { label: 'Draft Only', value: 'Draft' as PhotoFilter },
  { label: 'Live Only', value: 'Live' as PhotoFilter },
];

const validPhotoTypes = ['image/png', 'image/jpg', 'image/jpeg'];
const maxPhotoSizeInBytes = 15 * 1024 * 1024;

const PhotoStatusBadge = ({ status }: { status: PhotoStatus }) => (
  <DMText
    as="span"
    className={`absolute left-[1vw] top-[1.4vh] inline-flex items-center rounded-full px-[0.9vw] py-[0.65vh] text-[1.35vh] font-bold leading-none shadow-sm ${
      status === 'Live' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-700'
    }`}
  >
    {status === 'Live' ? '• Live' : 'Draft'}
  </DMText>
);

const GalleryPhotoPreviewButton = ({
  photo,
  onPreview,
}: {
  photo: InfrastructurePhotoItem;
  onPreview: (photo: InfrastructurePhotoItem) => void;
}) => {
  const [hasImageError, setHasImageError] = React.useState(false);

  React.useEffect(() => {
    setHasImageError(false);
  }, [photo.src]);

  const canPreview = Boolean(photo.src) && !hasImageError;

  return (
    <DMButton
      type="button"
      onClick={() => {
        if (canPreview) {
          onPreview(photo);
        }
      }}
      className={`h-full w-full ${
        canPreview ? 'cursor-pointer' : 'cursor-default'
      }`}
      aria-label={
        canPreview
          ? 'Preview infrastructure photo'
          : 'No infrastructure photo available'
      }
    >
      {!photo.src || hasImageError ? (
        <DMDiv
          className={`flex h-full w-full items-center justify-center bg-gray-100 text-[1.8vh] font-bold text-gray-500 ${
            photo.status === 'Draft' ? 'grayscale' : ''
          }`}
        >
          No Image
        </DMDiv>
      ) : (
        <DMImage
          src={photo.src}
          alt={`${photo.status} infrastructure`}
          className={`h-full w-full object-cover ${
            photo.status === 'Draft' ? 'grayscale' : ''
          }`}
          onError={() => setHasImageError(true)}
        />
      )}
    </DMButton>
  );
};

const InfrastructurePhotoTile = ({
  photo,
  onDelete,
  onToggleStatus,
  onPreview,
}: {
  photo: InfrastructurePhotoItem;
  onDelete: (id: number) => void;
  onToggleStatus: (id: number) => void;
  onPreview: (photo: InfrastructurePhotoItem) => void;
}) => (
  <DMDiv
    as="article"
    className="rounded-[8px] border border-gray-300 bg-white p-[1vh] shadow-[0_18px_32px_rgba(0,0,0,0.18)]"
  >
    <DMDiv className="relative h-[30vh] overflow-hidden rounded-[8px] bg-gray-100">
      <GalleryPhotoPreviewButton photo={photo} onPreview={onPreview} />
      <PhotoStatusBadge status={photo.status} />
    </DMDiv>
    <DMDiv className="mt-[1.4vh] flex items-center gap-[1vw] px-[0.4vw] pb-[0.3vh]">
      <DMButton
        type="button"
        onClick={() => onDelete(photo.id)}
        aria-label="Delete photo"
        className="flex h-[4.2vh] w-[4.2vh] shrink-0 items-center justify-center rounded-full bg-gray-50 text-red-500 transition-colors hover:bg-red-50"
      >
        <Trash2 className="h-[2vh] w-[2vh]" strokeWidth={2} />
      </DMButton>
      <DMButton
        type="button"
        onClick={() => onToggleStatus(photo.id)}
        className="flex h-[4.2vh] flex-1 items-center justify-center rounded-full bg-primary-main px-[1.4vw] text-[1.45vh] font-medium leading-none text-white transition-opacity hover:opacity-90"
      >
        {photo.status === 'Live' ? 'Unpublish' : 'Publish'}
      </DMButton>
    </DMDiv>
  </DMDiv>
);

const UploadPhotoModal = ({
  isOpen,
  previewUrl,
  isSubmitting,
  fileInputId,
  onClose,
  onFileChange,
  onDropFile,
  onPreview,
  onSubmit,
}: {
  isOpen: boolean;
  previewUrl: string;
  isSubmitting: boolean;
  fileInputId: string;
  onClose: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDropFile: (files: FileList | null) => void;
  onPreview: () => void;
  onSubmit: (status: PhotoStatus) => void;
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <DMDiv className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 px-[3vw] py-[4vh]">
      <DMDiv className="w-[44.5vw] min-w-[520px] max-w-[680px] rounded-[8px] bg-white px-[1.7vw] pb-[2.7vh] pt-[2.8vh] shadow-[0_18px_46px_rgba(0,0,0,0.24)]">
        <DMDiv className="mb-[2.4vh] flex items-center justify-between">
          <DMText
            as="h2"
            className="text-[2.6vh] font-bold leading-none text-gray-950"
          >
            Upload image
          </DMText>
          <DMButton
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex h-[3.2vh] w-[3.2vh] items-center justify-center text-gray-950 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close upload image"
          >
            <X className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.4} />
          </DMButton>
        </DMDiv>

        {previewUrl ? (
          <DMDiv className="relative h-[34vh] overflow-hidden rounded-[8px] bg-gray-100">
            <DMButton
              type="button"
              onClick={onPreview}
              className="block h-full w-full"
              aria-label="Preview selected infrastructure photo"
            >
              <DMImage
                src={previewUrl}
                alt="Selected infrastructure preview"
                className="h-full w-full object-cover"
              />
            </DMButton>
            <DMButton
              type="button"
              className="absolute right-[1vw] top-[1.3vh] flex h-[4.3vh] w-[4.3vh] items-center justify-center rounded-full bg-white text-primary-main shadow-sm"
              aria-label="Enhance selected photo"
            >
              <Sparkles className="h-[2.1vh] w-[2.1vh]" strokeWidth={2.2} />
            </DMButton>
          </DMDiv>
        ) : (
          <DMLabel
            htmlFor={fileInputId}
            onDragOver={event => event.preventDefault()}
            onDrop={event => {
              event.preventDefault();
              onDropFile(event.dataTransfer.files);
            }}
            className="flex h-[34vh] cursor-pointer flex-col items-center justify-center rounded-[8px] border border-dashed border-gray-400 bg-white text-center"
          >
            <DMInput
              id={fileInputId}
              type="file"
              accept="image/png,image/jpg,image/jpeg"
              className="hidden"
              onChange={onFileChange}
            />
            <DMText className="text-[1.85vh] font-bold leading-tight text-gray-950">
              Choose a file or drag & drop it here
            </DMText>
            <DMText className="mt-[2vh] text-[1.35vh] font-medium leading-none text-gray-500">
              png, jpg, jpeg (Max 15mb)
            </DMText>
            <DMText
              as="span"
              className="mt-[3vh] flex h-[5.8vh] min-w-[15.6vw] items-center justify-center gap-[0.65vw] rounded-full border border-primary-main bg-white px-[2vw] text-[1.55vh] font-semibold text-primary-main"
            >
              <UploadCloud className="h-[2vh] w-[2vh]" strokeWidth={2.2} />
              Upload Photo
            </DMText>
          </DMLabel>
        )}

        <DMDiv className="mt-[3vh] grid grid-cols-2 gap-[1vw]">
          <DMButton
            type="button"
            onClick={() => onSubmit('Draft')}
            disabled={isSubmitting}
            className="h-[6.2vh] rounded-full bg-gray-100 text-[1.75vh] font-semibold text-gray-950 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Draft
          </DMButton>
          <DMButton
            type="button"
            onClick={() => onSubmit('Live')}
            disabled={isSubmitting}
            className="h-[6.2vh] rounded-full bg-primary-main text-[1.75vh] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Publish
          </DMButton>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};

export function InfrastructurePhotoManager() {
  const { categorySlug } = useParams();
  const location = useLocation();
  const authData = useAuth();
  const routeCategory = photoCategories.find(
    item => item.slug === categorySlug
  );
  const selectedCategory =
    routeCategory ||
    (location.state as { category?: InfrastructurePhotoCategory } | null)
      ?.category ||
    photoCategories[0];

  const [activeFilter, setActiveFilter] = React.useState<PhotoFilter>('all');
  const [photos, setPhotos] = React.useState<InfrastructurePhotoItem[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = React.useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = React.useState<File | null>(
    null
  );
  const [selectedPhotoPreview, setSelectedPhotoPreview] = React.useState('');
  const [isSubmittingPhoto, setIsSubmittingPhoto] = React.useState(false);
  const [photoIdToDelete, setPhotoIdToDelete] = React.useState<number | null>(
    null
  );
  const [isDeletingPhoto, setIsDeletingPhoto] = React.useState(false);
  const [previewImageUrl, setPreviewImageUrl] = React.useState('');
  const modalFileInputId = React.useId();
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalRecords, setTotalRecords] = React.useState(0);

  const handlePageSizeChange = React.useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(1);
  }, []);

  React.useEffect(
    () => () => {
      if (selectedPhotoPreview) {
        URL.revokeObjectURL(selectedPhotoPreview);
      }
    },
    [selectedPhotoPreview]
  );

  const fetchPhotos = React.useCallback(
    async (currentPage: number, currentSize: number) => {
      setIsLoadingPhotos(true);

      try {
        const imageType = encodeURIComponent(selectedCategory.imageType);
        const response = await httpclient().get(
          `${instituteGalleryApi.list}?image_type=${imageType}&page_index=${currentPage}&page_size=${currentSize}`,
          {
            headers: { image_type: selectedCategory.imageType },
          }
        );

        const responseData = response?.data?.data || {};

        // Dynamically look for the nested array in the data object
        const arrayKey = Object.keys(responseData).find(key =>
          Array.isArray(responseData[key])
        );
        const itemsArray = arrayKey
          ? responseData[arrayKey]
          : Array.isArray(responseData)
          ? responseData
          : Array.isArray(response?.data)
          ? response.data
          : [];

        // Dynamically search for the total record count
        const totalRecords =
          responseData.Total ||
          responseData.total_count ||
          responseData.total ||
          itemsArray.length ||
          0;

        // Map and set the photos list
        setPhotos(
          itemsArray
            .map(mapGalleryImageToPhoto)
            .filter((photo: any) => photo.id)
        );

        setTotalRecords(totalRecords);
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Unable to fetch gallery images'),
          'error'
        );
        setPhotos([]);
        setTotalRecords(0);
      } finally {
        setIsLoadingPhotos(false);
      }
    },
    [selectedCategory.imageType]
  );

  React.useEffect(() => {
    fetchPhotos(pageIndex, pageSize);
  }, [
    authData?.BRANCH_ID,
    authData?.INSTITUTE_ID,
    fetchPhotos,
    pageIndex,
    pageSize,
  ]);

  const filteredPhotos = React.useMemo(() => {
    if (activeFilter === 'all') {
      return photos;
    }

    return photos.filter(photo => photo.status === activeFilter);
  }, [activeFilter, photos]);
  const hasReachedPhotoLimit = photos.length >= 10;

  const handleDelete = (id: number) => {
    setPhotoIdToDelete(id);
  };

  const handleCancelDelete = () => {
    if (isDeletingPhoto) {
      return;
    }

    setPhotoIdToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!photoIdToDelete) {
      return;
    }

    setIsDeletingPhoto(true);

    try {
      await httpclient({ dbName: 'masterdb' }).delete(
        instituteGalleryApi.delete(photoIdToDelete),
        {
          headers: { institute_gallery_id: photoIdToDelete },
        }
      );
      setPhotos(currentPhotos =>
        currentPhotos.filter(photo => photo.id !== photoIdToDelete)
      );
      setPhotoIdToDelete(null);
      showGlobalModal('Photo deleted successfully');
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to delete photo'),
        'error'
      );
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const handleToggleStatus = (id: number) => {
    const selectedPhoto = photos.find(photo => photo.id === id);

    if (!selectedPhoto) {
      return;
    }

    const isPublishing = selectedPhoto.status === 'Draft';

    showGlobalModal(
      isPublishing
        ? 'Are you sure you want to publish this photo?'
        : 'Are you sure you want to unpublish this photo?',
      'warning',
      {
        showConfirm: true,
        confimLabel: isPublishing ? 'Publish' : 'Unpublish',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          try {
            await httpclient().post(
              instituteGalleryApi.publish(id, isPublishing),
              {},
              { headers: { institute_gallery_id: id } }
            );
            setPhotos(currentPhotos =>
              currentPhotos.map(photo =>
                photo.id === id
                  ? { ...photo, status: isPublishing ? 'Live' : 'Draft' }
                  : photo
              )
            );
            showGlobalModal(
              isPublishing
                ? 'Photo published successfully'
                : 'Photo unpublished successfully'
            );
          } catch (error: any) {
            showGlobalModal(
              getApiErrorMessage(
                error,
                'Unable to update photo publish status'
              ),
              'error'
            );
          }
        },
        onCancel: () => true,
      }
    );
  };

  const closeUploadModal = () => {
    if (isSubmittingPhoto) {
      return;
    }
    if (selectedPhotoPreview) {
      URL.revokeObjectURL(selectedPhotoPreview);
    }
    setSelectedPhotoFile(null);
    setSelectedPhotoPreview('');
    setIsUploadModalOpen(false);
  };

  const handleSelectedFile = (file?: File) => {
    if (!file) {
      return;
    }

    if (!validPhotoTypes.includes(file.type)) {
      showGlobalModal('Only png, jpg, or jpeg files are allowed', 'error');
      return;
    }

    if (file.size > maxPhotoSizeInBytes) {
      showGlobalModal('File size exceeds 15MB', 'error');
      return;
    }

    if (selectedPhotoPreview) {
      URL.revokeObjectURL(selectedPhotoPreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setSelectedPhotoFile(file);
    setSelectedPhotoPreview(previewUrl);
  };

  const handleUploadFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleSelectedFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const handleSubmitPhoto = async (status: PhotoStatus) => {
    if (!selectedPhotoFile) {
      showGlobalModal('Please select an image before saving', 'warning');
      return;
    }

    setIsSubmittingPhoto(true);
    showGlobalModal(
      <DMDiv>
        <DMText className="text-[2.6vh] font-bold leading-tight text-gray-950">
          Enhancing Photos..
        </DMText>
        <DMText className="mt-[1.1vh] text-[1.75vh] font-medium leading-tight text-gray-500">
          Please wait while we enhance your uploaded photos
        </DMText>
      </DMDiv>,
      'loading',
      { showConfirm: true }
    );

    try {
      const formData = new FormData();
      formData.append('dest', 'WEBSITE');
      formData.append('branch_id', authData?.BRANCH_ID as any);
      formData.append('database', authData?.DATABASE as any);
      formData.append('files', selectedPhotoFile);

      const response = await apiFileUpload(
        formData,
        'upload',
        '',
        '',
        true,
        false,
        true
      );
      const uploadedUrl = extractUploadedUrl(response);

      if (!uploadedUrl) {
        showGlobalModal('Unable to read uploaded image URL', 'error');
        return;
      }

      const upsertResponse = await httpclient().post(
        instituteGalleryApi.upsert,
        {
          IMAGE_TYPE: selectedCategory.imageType,
          IMAGE_URLS: [uploadedUrl],
        }
      );
      const savedImages = upsertResponse?.data?.data?.images || [];

      if (status === 'Live') {
        await Promise.all(
          savedImages.map((image: any) => {
            const galleryId = Number(
              image?.INSTITUTE_GALLERY_ID || image?.institute_gallery_id || 0
            );
            return galleryId
              ? httpclient().post(
                  instituteGalleryApi.publish(galleryId, true),
                  {},
                  { headers: { institute_gallery_id: galleryId } }
                )
              : Promise.resolve();
          })
        );
      }

      await fetchPhotos(pageIndex, pageSize);

      if (selectedPhotoPreview) {
        URL.revokeObjectURL(selectedPhotoPreview);
      }
      setSelectedPhotoFile(null);
      setSelectedPhotoPreview('');
      setIsUploadModalOpen(false);
      showGlobalModal(
        status === 'Live' ? 'Photos published' : 'Photo saved as draft'
      );
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to upload photo'),
        'error'
      );
    } finally {
      setIsSubmittingPhoto(false);
    }
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-900"
    >
      <DMDiv className="mb-[2.4vh] flex shrink-0 items-start justify-between gap-[2vw]">
        <DMDiv>
          <DigitalMarketingBreadcrumb
            className="mb-[1.6vh]"
            items={[
              {
                label: 'Edit Website Content',
                path: EDIT_WEBSITE_CONTENT_BASE_PATH,
              },
              {
                label: 'Upload Infrastructure Photos',
                path: buildEditWebsiteContentPath(
                  'upload-infrastructure-photos'
                ),
              },
              { label: selectedCategory.title },
            ]}
          />
          <DMText
            as="h1"
            className="text-[3.9vh] font-bold leading-tight text-gray-950"
          >
            {selectedCategory.title}
          </DMText>
          <DMText className="mt-[0.6vh] text-[1.85vh] font-medium leading-tight text-gray-600">
            {selectedCategory.description}
          </DMText>
        </DMDiv>

        <DMButton
          type="button"
          onClick={() => {
            if (!hasReachedPhotoLimit) {
              setIsUploadModalOpen(true);
            }
          }}
          disabled={hasReachedPhotoLimit}
          className="mt-[1vh] flex h-[6.7vh] items-center justify-center gap-[0.9vw] rounded-full bg-primary-main px-[2.3vw] text-[1.75vh] font-semibold leading-none text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          title={
            hasReachedPhotoLimit
              ? 'Maximum 10 photos can be uploaded'
              : undefined
          }
        >
          <Plus className="h-[2.5vh] w-[2.5vh]" strokeWidth={2.2} />
          <DMText as="span">Add photo</DMText>
        </DMButton>
      </DMDiv>

      <ManagementFilterBar
        className="mb-[2.4vh]"
        options={photoFilterOptions}
        selectedValue={activeFilter}
        allValue="all"
        onChange={setActiveFilter}
      />

      <DMDiv
        as="section"
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-gray-300 bg-gray-50 px-[1.5vw] py-[2.4vh]"
      >
        <DMDiv className="min-h-0 flex-1 overflow-y-auto">
          {isLoadingPhotos ? (
            <DMDiv className="flex h-full min-h-[28vh] items-center justify-center text-[1.8vh] font-semibold text-gray-500">
              Loading gallery photos...
            </DMDiv>
          ) : filteredPhotos.length ? (
            <DMDiv className="grid grid-cols-3 gap-x-[1.35vw] gap-y-[2vh]">
              {filteredPhotos.map(photo => (
                <InfrastructurePhotoTile
                  key={photo.id}
                  photo={photo}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onPreview={selectedPhoto =>
                    setPreviewImageUrl(selectedPhoto.src)
                  }
                />
              ))}
            </DMDiv>
          ) : (
            <DMDiv className="flex h-full min-h-[28vh] flex-col items-center justify-center text-center">
              <DMText className="text-[2.1vh] font-bold leading-tight text-gray-950">
                No photos found
              </DMText>
              <DMText className="mt-[0.9vh] text-[1.55vh] font-medium leading-tight text-gray-500">
                Upload photos to build this gallery section.
              </DMText>
            </DMDiv>
          )}
        </DMDiv>

        {(isLoadingPhotos || totalRecords > 0) && (
          <DMDiv className="mt-[2vh] shrink-0">
            <PaginationBar
              key={`${selectedCategory.slug}-${pageSize}`}
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPageIndex}
              onPageSizeChange={handlePageSizeChange}
              itemName="photos"
            />
          </DMDiv>
        )}
      </DMDiv>

      <UploadPhotoModal
        isOpen={isUploadModalOpen}
        previewUrl={selectedPhotoPreview}
        isSubmitting={isSubmittingPhoto}
        fileInputId={modalFileInputId}
        onClose={closeUploadModal}
        onFileChange={handleUploadFileChange}
        onDropFile={files => handleSelectedFile(files?.[0])}
        onPreview={() => setPreviewImageUrl(selectedPhotoPreview)}
        onSubmit={handleSubmitPhoto}
      />
      <DigitalMarketingDeleteModal
        isOpen={photoIdToDelete !== null}
        title="Are you sure you want to delete this photo?"
        isDeleting={isDeletingPhoto}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <ImagePreviewModal
        imageUrl={previewImageUrl}
        alt="Infrastructure photo"
        onClose={() => setPreviewImageUrl('')}
      />
    </DMDiv>
  );
}

export function InfrastructurePhotos() {
  const authData = useAuth();
  const [categories, setCategories] = React.useState<
    InfrastructurePhotoCategory[]
  >(() => buildCategoriesWithStats([]));
  const [isLoadingStats, setIsLoadingStats] = React.useState(false);

  React.useEffect(() => {
    const fetchGalleryStats = async () => {
      setIsLoadingStats(true);

      try {
        const response = await httpclient().get(instituteGalleryApi.stats);
        setCategories(buildCategoriesWithStats(getResponseList(response)));
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Unable to fetch gallery stats'),
          'error'
        );
        setCategories(buildCategoriesWithStats([]));
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchGalleryStats();
  }, [authData?.BRANCH_ID, authData?.INSTITUTE_ID]);

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-900"
    >
      <DMDiv className="mb-[2.4vh] shrink-0">
        <DigitalMarketingBreadcrumb
          className="mb-[1.6vh]"
          items={[
            {
              label: 'Edit Website Content',
              path: EDIT_WEBSITE_CONTENT_BASE_PATH,
            },
            { label: 'Upload Infrastructure Photos' },
          ]}
        />

        <DMText
          as="h1"
          className="text-[3.9vh] font-bold leading-tight text-gray-950"
        >
          Upload Infrastructure Photos
        </DMText>
        <DMText className="mt-[0.6vh] text-[1.85vh] font-medium leading-tight text-gray-600">
          Turn your school website into a lead engine! Showcase your
          Institution&apos;s infrastructure to upto 1 lakh parents!!
        </DMText>
      </DMDiv>

      <DMDiv
        as="section"
        className="min-h-0 flex-1 overflow-y-auto rounded-[18px] border border-gray-300 bg-gray-50 px-[1.5vw] py-[2.4vh]"
      >
        {isLoadingStats ? (
          <DMDiv className="flex h-full min-h-[32vh] items-center justify-center text-[1.8vh] font-semibold text-gray-500">
            Loading gallery stats...
          </DMDiv>
        ) : (
          <DMDiv className="grid grid-cols-3 gap-x-[1.35vw] gap-y-[2vh]">
            {categories.map(item => (
              <InfrastructurePhotoCard
                key={`${item.title}-${item.indexLabel}`}
                item={item}
              />
            ))}
          </DMDiv>
        )}
      </DMDiv>
    </DMDiv>
  );
}
